import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import GlobalLayout from './GlobalLayout';
import './Challenges.css';

import crownIcon from '../assets/icons/crown.svg';
import trophyIcon from '../assets/icons/trophy.svg';
import toolsIcon from '../assets/icons/tools.svg';
import searchIcon from '../assets/icons/search.svg';

// --- NEU: Helper Funktion für Mehrsprachigkeit ---
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  const localizedField = `${field}_${code}`;
  
  // Prüfen ob das feld_lang (z.B. name_en) existiert und nicht leer/null ist
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }
  // Fallback auf Default Feld (z.B. name)
  return item[field] || '';
};

const Challenges = () => {
  const navigate = useNavigate();
  const { getChallenges, getCurrentChallenges, setProgress, makeAuthenticatedRequest } = useAuth();
  
  // --- ÄNDERUNG: currentLanguage holen ---
  const { t, currentLanguage } = useLanguage();
  
  const [challenges, setChallenges] = useState([]);
  const [currentChallenges, setCurrentChallenges] = useState([]);
  const [mandatoryCollections, setMandatoryCollections] = useState([]);
  const challengesRef = useRef(null);
  const mandatoryRef  = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [allChallengesData, setAllChallengesData] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Default categories with icons and colors (fallback if API doesn't provide them)
  const defaultCategories = [
    { 
      name: 'Must-See', 
      icon: '🔥', 
      color: '#EA580C', 
      bgColor: '#FFEDD5', 
      borderColor: '#FED7AA',
      tags: ['important', 'featured', 'must-see']
    },
    // ... andere Kategorien bleiben gleich ...
  ];

  // --- ÄNDERUNG: Reload bei Sprachwechsel ---
  useEffect(() => {
    fetchChallenges(null, true);
  }, [currentLanguage]); // Hinzugefügt: currentLanguage

  const fetchChallenges = async (categoryFilter = null, forceRefresh = false) => {
    if (!forceRefresh && challenges.length > 0) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const filters = {};
      if (categoryFilter && categoryFilter.tags && categoryFilter.tags.length > 0) {
        filters.tags = categoryFilter.tags;
      }
      
      const [challengesResponse, currentChallengesResponse, mandatoryResponse] = await Promise.all([
        getChallenges(filters),
        getCurrentChallenges(),
        makeAuthenticatedRequest('/mandatory-collections', { method: 'GET' }),
      ]);

      if (challengesResponse?.status === 'success' && challengesResponse?.data) {
        const rawChallengesData = challengesResponse.data;
        const challengesData = (Array.isArray(rawChallengesData) ? rawChallengesData : [])
          .filter(item => Number(item?.type) === 2);
        
        // --- ÄNDERUNG: Daten direkt hier übersetzen ---
        const localizedChallenges = challengesData.map(item => ({
          ...item,
          name: getLocalizedContent(item, 'name', currentLanguage) || item.name || item.title,
          title: getLocalizedContent(item, 'name', currentLanguage) || item.title || item.name,
          description: getLocalizedContent(item, 'description', currentLanguage) || item.description
        }));

        if (!categoryFilter) {
          setAllChallengesData(localizedChallenges);
          
          // Build available tags
          const filterSet = new Set();
          (Array.isArray(localizedChallenges) ? localizedChallenges : []).forEach(item => {
            (Array.isArray(item?.tags) ? item.tags : []).forEach(tag => {
              // Helper nutzen für Tag-Namen
              const rawName = typeof tag === 'string' ? tag : (tag?.name || tag?.tag || '');
              
              // Falls Tag ein Objekt ist, versuchen wir es zu übersetzen
              let tagName = rawName;
              if (typeof tag === 'object' && tag !== null) {
                 tagName = getLocalizedContent(tag, 'name', currentLanguage) || rawName;
              }

              const tagType = tag?.type || tag?.type_id;
              if (tagName && tagName.trim() && tagType === 1) {
                filterSet.add(tagName.trim());
              }
            });
            // Nested elements tags...
            (Array.isArray(item?.elements) ? item.elements : []).forEach(el => {
              (Array.isArray(el?.tags) ? el.tags : []).forEach(tag => {
                 let tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.tag || '');
                 if (typeof tag === 'object' && tag !== null) {
                    tagName = getLocalizedContent(tag, 'name', currentLanguage) || tagName;
                 }
                 const tagType = tag?.type || tag?.type_id;
                 if (tagName && tagName.trim() && tagType === 1) {
                   filterSet.add(tagName.trim());
                 }
              });
            });
          });
          const availableTagsArray = Array.from(filterSet).sort();
          setAvailableTags(availableTagsArray);
        }
        
        setChallenges(localizedChallenges);
      }

      if (currentChallengesResponse?.status === 'success' && currentChallengesResponse?.data) {
        const localizedCurrent = (currentChallengesResponse.data || []).map(item => ({
            ...item,
            name: getLocalizedContent(item, 'name', currentLanguage) || item.name,
            description: getLocalizedContent(item, 'description', currentLanguage) || item.description
        }));
        setCurrentChallenges(localizedCurrent);
      }

      if (mandatoryResponse?.status === 'success' && Array.isArray(mandatoryResponse?.data)) {
        const localizedMandatory = mandatoryResponse.data.map(item => ({
          ...item,
          name: getLocalizedContent(item, 'name', currentLanguage) || item.name || item.title,
          title: getLocalizedContent(item, 'name', currentLanguage) || item.title || item.name,
          description: getLocalizedContent(item, 'description', currentLanguage) || item.description,
        }));
        setMandatoryCollections(localizedMandatory);
      }
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
      setError(t('failedToLoadChallenges'));
      // Fallback data (Mock)
      setChallenges([
        {
          id: 6,
          name: t('toolCachingChallenge') || 'Tool Caching Challenge',
          description: t('toolCachingDesc') || 'Find specific tools in your work place!',
          hash_id: '32422234sdfsdf',
          url_thumbnail: 'https://app.hejvi.de/api/img/different-tools-are-on-the-workbench-focus-on-the-2025-01-08-03-32-12-utc.jpg',
          level_complexity: 4,
          experience_points: 500,
          duration: 200,
          challenge_start: '2025-08-20 10:49:00',
          challenge_end: '2025-08-22 15:49:00',
          progress: '0.0000',
          favourite_value: null
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Local filtering
  useEffect(() => {
    if (!Array.isArray(allChallengesData) || allChallengesData.length === 0) return;
    if (!selectedTags || selectedTags.length === 0) {
      setChallenges(allChallengesData);
      return;
    }
    const filtered = allChallengesData.filter(item => {
      const itemTags = (Array.isArray(item?.tags) ? item.tags : []).map(tag => {
        // Tag Übersetzung beachten
        let tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.tag || '');
        if (typeof tag === 'object' && tag !== null) {
            tagName = getLocalizedContent(tag, 'name', currentLanguage) || tagName;
        }
        return tagName && tagName.trim() ? tagName.trim() : null;
      }).filter(Boolean);
      
      const nestedTags = (Array.isArray(item?.elements) ? item.elements : [])
        .flatMap(el => (Array.isArray(el?.tags) ? el.tags : []).map(tag => {
          let tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.tag || '');
          if (typeof tag === 'object' && tag !== null) {
             tagName = getLocalizedContent(tag, 'name', currentLanguage) || tagName;
          }
          return tagName && tagName.trim() ? tagName.trim() : null;
        })).filter(Boolean);
      
      const combined = [...itemTags, ...nestedTags];
      return selectedTags.some(tag => combined.includes(tag));
    });
    
    setChallenges(filtered);
  }, [selectedTags, allChallengesData, currentLanguage]);

  const handleChallengeClick = async (challenge) => {
    const hashId = challenge.hash_id || challenge.hashId || challenge.id;
    if (!hashId) return;
    
    try {
      if (parseFloat(challenge.progress || 0) === 0) {
        await setProgress(hashId, 1);
      }
      navigate(`/course/${hashId}`, { state: { collectionData: challenge, sourcePage: '/challenges' } });
    } catch (error) {
      navigate(`/course/${hashId}`, { state: { collectionData: challenge, sourcePage: '/challenges' } });
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0 min';
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };

  const formatTimeLeft = (endDate) => {
    if (!endDate) return t('noDeadline');
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    if (diff <= 0) return t('expired');
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${t('left') || 'left'}`;
    if (hours > 0) return `${hours}h ${t('left') || 'left'}`;
    return t('lessThan1hLeft');
  };

  const getLevelText = (level) => {
    const levels = {
      1: t('beginner'),
      2: t('intermediate'), 
      3: t('advanced'),
      4: t('expert')
    };
    return levels[level] || t('beginner');
  };

  const transformChallenges = (challengeList) => {
    return challengeList.map(challenge => {
      const hashId = challenge.hash_id || challenge.hashId || challenge.id;
      
      // Titel & Beschreibung sind bereits oben in fetchChallenges übersetzt worden!
      // Wir nutzen sie direkt.
      
      return {
        id: challenge.id,
        hash_id: hashId,
        type: challenge.type,
        // HIER WIRD DAS ÜBERSETZTE GENUTZT
        title: challenge.name || challenge.title, 
        desc: challenge.description,
        
        progressText: `${Math.round(parseFloat(challenge.progress || 0))}%`,
        xpText: `+${challenge.experience_points} XP`,
        status: parseFloat(challenge.progress || 0) === 100 ? t('completed') : 
                parseFloat(challenge.progress || 0) > 0 ? t('continue') : t('start'),
        statusVariant: parseFloat(challenge.progress || 0) === 100 ? 'success' : 
                      parseFloat(challenge.progress || 0) > 0 ? 'secondary' : 'primary',
        image: challenge.url_thumbnail,
        duration: formatDuration(challenge.duration),
        level: getLevelText(challenge.level_complexity),
        timeLeft: formatTimeLeft(challenge.challenge_end),
        isFavorite: challenge.favourite_value === 1,
        progress: parseFloat(challenge.progress || 0),
        originalChallenge: challenge
      };
    });
  };

  const transformedChallenges = transformChallenges(challenges);
  const transformedCurrentChallenges = transformChallenges(currentChallenges);

  const calculateWeeklyProgress = () => {
    const totalChallenges = transformedChallenges.length;
    const completedChallenges = transformedChallenges.filter(challenge => 
      parseFloat(challenge.progress || 0) === 100
    ).length;
    const totalProgress = transformedChallenges.reduce((sum, challenge) => {
      return sum + parseFloat(challenge.progress || 0);
    }, 0);
    const averageProgress = totalChallenges > 0 ? totalProgress / totalChallenges : 0;
    return {
      total: totalChallenges,
      completed: completedChallenges,
      progressPercentage: Math.round(averageProgress)
    };
  };

  const weeklyStats = calculateWeeklyProgress();

  if (loading) {
    return (
      <GlobalLayout title={t('challenges')} showNavbar>
        <div className="challenges-page">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('loadingChallenges')}</p>
          </div>
        </div>
      </GlobalLayout>
    );
  }

  if (error) {
    return (
      <GlobalLayout title={t('challenges')} showNavbar>
        <div className="challenges-page">
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => fetchChallenges(null, true)} className="ch-btn ch-btn-primary">{t('retry')}</button>
          </div>
        </div>
      </GlobalLayout>
    );
  }

  return (
    <GlobalLayout title={t('challenges')} showNavbar>
      <div className="challenges-page">
        {/* Current Active Challenges */}
        {transformedCurrentChallenges.length > 0 && (
          <section className="ch-card ch-feature">
            <div className="ch-feature-left">
              <div className="ch-feature-icon-wrap">
                <img src={crownIcon} alt="crown" />
              </div>
              <div className="ch-feature-texts">
                <h3 className="ch-feature-title">{t('activeChallenges')}</h3>
                <p className="ch-feature-sub">
                  {transformedCurrentChallenges[0]?.desc || t('completeChallengesEarnXP')}
                </p>
                <div className="ch-feature-meta">
                  <span className="ch-chip ch-chip-soft">
                    {transformedCurrentChallenges[0]?.timeLeft || t('noDeadline')}
                  </span>
                  <span className="ch-chip ch-chip-soft">
                    {transformedCurrentChallenges[0]?.xpText || '+50 XP'}
                  </span>
                </div>
              </div>
            </div>
            <div className="ch-feature-right">
              <span className="ch-badge ch-badge-orange">{t('active')}</span>
              <button 
                className="ch-btn ch-btn-orange"
                onClick={() => handleChallengeClick(transformedCurrentChallenges[0].originalChallenge)}
              >
                {transformedCurrentChallenges[0]?.status || t('startNow')}
              </button>
            </div>
          </section>
        )}

        {/* Weekly Progress */}
        <section className="ch-weekly">
          {/* Balken 1: Herausforderungen */}
          <div className="ch-weekly-top">
            <span
              className="ch-weekly-title"
              style={{ cursor: 'pointer' }}
              onClick={() => challengesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >{t('challengesProgressLabel') || t('weeklyProgress')}</span>
            <span className="ch-weekly-count">
              <img src={trophyIcon} alt="trophy" /> {weeklyStats.completed} / {weeklyStats.total}
            </span>
          </div>
          <div className="ch-progress-bar">
            <div className="ch-progress-fill" style={{ width: `${weeklyStats.progressPercentage}%` }} />
          </div>

          {/* Balken 2: Pflichtkurse */}
          {mandatoryCollections.length > 0 && (() => {
            const totalMandatory = mandatoryCollections.length;
            // Abgeschlossen = alle Elemente haben max_progress >= 90 (Backend liefert completed_elements korrekt)
            const completedMandatory = mandatoryCollections.filter(c => {
              const total     = parseInt(c.total_elements     || 0);
              const completed = parseInt(c.completed_elements || 0);
              // Wenn total_elements bekannt: Element-Count nutzen (Anti-Cheat-sicher)
              if (total > 0) return completed >= total;
              // Fallback: progress-Feld (nur wenn keine Element-Daten da sind)
              return parseFloat(c.progress || 0) >= 90;
            }).length;
            const mandatoryPct = Math.round(completedMandatory / totalMandatory * 100);
            return (
              <div style={{ marginTop: 14 }}>
                <div className="ch-weekly-top">
                  <span
                    className="ch-weekly-title"
                    style={{ cursor: 'pointer' }}
                    onClick={() => mandatoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >{t('mandatoryProgressLabel') || 'Fortschritt Pflichtkurse'}</span>
                  <span className="ch-weekly-count">
                    <img src={trophyIcon} alt="trophy" /> {completedMandatory} / {totalMandatory}
                  </span>
                </div>
                <div className="ch-progress-bar">
                  <div className="ch-progress-fill" style={{ width: `${mandatoryPct}%`, background: mandatoryPct >= 100 ? '#10B981' : '#DC2626' }} />
                </div>
              </div>
            );
          })()}
        </section>

        {/* Tag Filters */}
        {availableTags.length > 0 && (
            <div className="tags-container">
              <div className="tags-scroll">
                <button
                  key="__all__"
                  className={`tag-button ${selectedTags.length === 0 ? 'active' : ''}`}
                  onClick={() => setSelectedTags([])}
                  title="Show all"
                >
                  #all
                </button>
                {availableTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      className={`tag-button ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        const next = isSelected
                          ? selectedTags.filter(tg => tg !== tag)
                          : [...selectedTags, tag];
                        setSelectedTags(next);
                      }}
                      title={`Filter by ${tag}`}
                    >
                      #{tag}
                    </button>
                  );
                })}
              </div>
            </div>
        )}

        {/* Pflichtkurse */}
        {mandatoryCollections.length > 0 && (
          <div>
            <div ref={mandatoryRef} className="ch-list-header" style={{ marginTop: 28 }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>🎯</span>
                <span>{t('mandatoryCourses') || 'Pflichtkurse'}</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#6B7280' }}>
                  ({mandatoryCollections.length})
                </span>
              </h4>
            </div>
            <div className="ch-list" style={{ marginTop: 8 }}>
              {mandatoryCollections.map((item) => {
                const hashId   = item.hash_id || item.hashId || item.id;
                const total     = parseInt(item.total_elements     || 0);
                const completed = parseInt(item.completed_elements || 0);

                // Chip-Anzeige: watch_seconds-basierte Abschlüsse (Anti-Cheat)
                const progressPct = total > 0
                  ? Math.round(completed / total * 100)
                  : Math.min(100, Math.round(parseFloat(item.progress || 0)));

                // Balken: AVG max_progress aller Elemente (zeigt partielle Beteiligung)
                // item.progress = vom Backend berechneter Durchschnitt der Element-Fortschritte
                // → gibt visuelles Feedback auch wenn noch kein Video vollständig abgeschlossen
                const barPct = Math.min(100, Math.max(0, Math.round(parseFloat(item.progress || 0))));

                // isDone über Elementzählung, Fallback auf progressPct
                const isDone = total > 0 ? (completed >= total) : (progressPct >= 90);
                return (
                  <div
                    key={item.id}
                    className="ch-item"
                    onClick={() => navigate(`/course/${hashId}`, { state: { collectionData: item, sourcePage: '/challenges' } })}
                  >
                    <div className="ch-item-icon">
                      {item.url_thumbnail ? (
                        <img src={item.url_thumbnail} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                      ) : (
                        <img src={toolsIcon} alt="icon" />
                      )}
                    </div>
                    <div className="ch-item-body">
                      <div className="ch-item-title">{item.title || item.name}</div>
                      <div className="ch-item-sub">{item.description}</div>
                      <div className="ch-item-chips">
                        {/* Fortschritt-Chip */}
                        <span className="ch-chip ch-chip-plain">
                          {total > 0 ? `${completed}/${total}` : `${progressPct}%`}
                        </span>
                        {/* XP-Chip */}
                        {item.experience_points > 0 && (
                          <span className="ch-chip ch-chip-soft">
                            <i><svg width="8" height="8" viewBox="0 0 14 13" fill="none"><path d="M7 0L8.5 4L12.5 4.5L9.5 7.5L10.5 13L7 10.5L3.5 13L4.5 7.5L1.5 4.5L5.5 4L7 0Z" fill="#FA7F29"/></svg></i>
                            +{item.experience_points} XP
                          </span>
                        )}
                        {/* Pflicht-Badge */}
                        <span className="ch-chip" style={{ background: '#FEE2E2', color: '#DC2626', borderColor: '#FECACA', fontSize: 10, padding: '1px 6px' }}>
                          {t('mandatory') || 'Pflicht'}
                        </span>
                      </div>
                      {/* Fortschrittsbalken — immer anzeigen, auch bei 0 % */}
                      <div style={{ height: 4, borderRadius: 2, background: '#E5E7EB', overflow: 'hidden', marginTop: 4 }}>
                        <div style={{
                          height: '100%',
                          width: `${barPct}%`,
                          background: isDone ? '#10B981' : '#FB923C',
                          borderRadius: 2,
                          transition: 'width 0.5s'
                        }} />
                      </div>
                    </div>
                    <div className="ch-item-cta">
                      {isDone ? (
                        <span className="ch-badge ch-badge-green">{t('completed') || 'Fertig'}</span>
                      ) : (
                        <button className="ch-btn ch-btn-orange" style={{ padding: '5px 10px', fontSize: 11 }}>
                          {progressPct > 0 ? (t('continue') || 'Weiter') : (t('start') || 'Starten')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Challenges header */}
        <div ref={challengesRef} className="ch-list-header">
          <h4>
            {t('currentChallenges') || t('allChallenges')}
            {' '}({transformedChallenges.length})
          </h4>
        </div>

        {/* All Challenges list */}
        <div className="ch-list">
          {transformedChallenges.length > 0 ? (
            transformedChallenges.map((item) => (
              <div key={item.id} className="ch-item" onClick={() => handleChallengeClick(item.originalChallenge)}>
                <div className="ch-item-icon">
                  {item.image ? (
                    <img src={item.image} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                  ) : (
                    <img src={toolsIcon} alt="icon" />
                  )}
                </div>
                <div className="ch-item-body">
                  <div className="ch-item-title">{item.title}</div>
                  <div className="ch-item-sub">{item.desc}</div>
                  <div className="ch-item-chips">
                    <span className="ch-chip ch-chip-plain">{item.progressText}</span>
                    <span className="ch-chip ch-chip-soft">
                      <i>
                        <svg width="8" height="8" viewBox="0 0 14 13" fill="none">
                          <path d="M7 0L8.5 4L12.5 4.5L9.5 7.5L10.5 13L7 10.5L3.5 13L4.5 7.5L1.5 4.5L5.5 4L7 0Z" fill="#FA7F29"/>
                        </svg>
                      </i>
                      {item.xpText}
                    </span>
                    <span className="ch-chip ch-chip-soft">{item.level}</span>
                    <span className="ch-chip ch-chip-soft">{item.timeLeft}</span>
                  </div>
                </div>
                <div className="ch-item-cta">
                  {item.statusVariant === 'success' ? (
                    <span className="ch-badge ch-badge-green">{item.status}</span>
                  ) : (
                    <button className={`ch-btn ${item.statusVariant === 'primary' ? 'ch-btn-primary' : 'ch-btn-secondary'}`}>
                      {item.status}
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="ch-empty-state">
              <p>{t('noChallenges')}</p>
              <button onClick={() => fetchChallenges(null, true)} className="ch-btn ch-btn-primary">{t('retry')}</button>
            </div>
          )}
        </div>

        {/* Discover more */}
        <div className="ch-discover">
          <button 
            className="ch-btn ch-btn-discover"
            style={{ visibility: 'hidden' }}
          >
            <img src={searchIcon} alt="search" />
            <span>{t('discoverMore')}</span>
          </button>
        </div>
      </div>
    </GlobalLayout>
  );
};

export default Challenges;